import { useEffect, useState } from "react";
import { Icon } from "./Icon";
import { useList } from "@/lib/api/queries";
import type { ServiceName } from "@/lib/api/services";

/**
 * Ma'lumotnomadan bitta yozuvni tanlash.
 *
 * Nega oddiy `<select>` emas: 1C dan 56 000 tovar va 139 000
 * kontragent keladi. Ularni bitta ro'yxatga yuklash brauzerni
 * to'xtatib qo'yardi, shuning uchun qidiruv SERVERDA bajariladi va
 * faqat birinchi o'nta natija ko'rsatiladi.
 *
 * Tanlangandan keyin maydon YOPILADI va tanlov matn bo'lib qoladi:
 * ochiq qolgan ro'yxat keyingi maydonni to'sib turardi.
 */
export interface PickedEntity {
  id: string;
  label: string;
}

interface EntityPickerProps<T> {
  service: ServiceName;
  path: string;
  /** Qo'shimcha filtrlar (masalan `is_active: true`). */
  query?: Record<string, string | number | boolean | undefined>;
  placeholder: string;
  value: PickedEntity | null;
  onChange: (value: PickedEntity | null) => void;
  /** Yozuvdan ko'rinadigan matn yasaydi. */
  render: (row: T) => string;
  disabled?: boolean;
}

export function EntityPicker<T extends { id: string }>({
  service,
  path,
  query = {},
  placeholder,
  value,
  onChange,
  render,
  disabled,
}: EntityPickerProps<T>) {
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Qidiruv so'zi bo'lmaguncha so'rov YUBORILMAYDI: bo'sh maydon
  // uchun minglab yozuvni tortib olish keraksiz.
  const results = useList<T>(
    service,
    path,
    { ...query, q: needle, limit: 10 },
    { enabled: !disabled && value === null && needle.length >= 2 },
  );

  if (value) {
    return (
      <div className="picker-chosen">
        <span>{value.label}</span>
        <button
          type="button"
          className="btn btn-text btn-sm"
          onClick={() => {
            onChange(null);
            setSearch("");
            setNeedle("");
          }}
          disabled={disabled}
          aria-label="Tanlovni bekor qilish"
        >
          <Icon name="close" size={14} />
        </button>
      </div>
    );
  }

  const rows = results.data?.items ?? [];

  return (
    <div className="picker">
      <div className="search-box">
        <Icon name="search" size={16} className="icon-inside" />
        <input
          className="input"
          placeholder={placeholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          disabled={disabled}
        />
      </div>
      {needle.length >= 2 && (
        <div className="picker-list">
          {results.isLoading && <div className="picker-empty">Qidirilmoqda…</div>}
          {!results.isLoading && rows.length === 0 && (
            <div className="picker-empty">Topilmadi</div>
          )}
          {rows.map((row) => (
            <button
              key={row.id}
              type="button"
              className="picker-item"
              onClick={() => onChange({ id: row.id, label: render(row) })}
            >
              {render(row)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
